import { movies, type Movie } from "@/mocks/movies";

export type SortKey = "latest" | "popular" | "rating";

export interface MovieQuery {
  type?: "movie" | "tv" | "";
  region?: string;
  year?: string;
  genre?: string;
  keyword?: string;
  sort?: SortKey;
}

const REGION_GROUPS: Record<string, string[]> = {
  欧美: ["美国", "英国", "法国"],
};

export const allRegions: string[] = Array.from(new Set(movies.map((m) => m.region)));
export const allYears: string[] = Array.from(new Set(movies.map((m) => String(m.year))))
  .sort((a, b) => Number(b) - Number(a));
export const allGenres: string[] = Array.from(new Set(movies.flatMap((m) => m.genres)));

export function getMovieById(id: string): Movie | undefined {
  return movies.find((m) => m.id === id);
}

export function getRelated(movie: Movie, limit = 8): Movie[] {
  const scored = movies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      const genreScore = m.genres.filter((g) => movie.genres.includes(g)).length * 10;
      const regionScore = m.region === movie.region ? 3 : 0;
      const typeScore = m.type === movie.type ? 2 : 0;
      return { m, score: genreScore + regionScore + typeScore };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || b.m.popularity - a.m.popularity);
  return scored.slice(0, limit).map((x) => x.m);
}

function matchRegion(m: Movie, region: string): boolean {
  if (!region || region === "全部") return true;
  const group = REGION_GROUPS[region];
  if (group) return group.includes(m.region);
  return m.region === region;
}

export function sortMovies(list: Movie[], sort: SortKey = "latest"): Movie[] {
  const copy = [...list];
  if (sort === "popular") {
    return copy.sort((a, b) => b.popularity - a.popularity);
  }
  if (sort === "rating") {
    return copy.sort((a, b) => b.rating - a.rating);
  }
  return copy.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

export function filterMovies(query: MovieQuery): Movie[] {
  const { type, region, year, genre, keyword, sort } = query;
  const kw = (keyword || "").trim().toLowerCase();
  const result = movies.filter((m) => {
    if (type && m.type !== type) return false;
    if (region && !matchRegion(m, region)) return false;
    if (year && String(m.year) !== year) return false;
    if (genre && !m.genres.includes(genre)) return false;
    if (kw) {
      const haystack = `${m.title} ${m.synopsis} ${m.genres.join(" ")} ${m.region}`.toLowerCase();
      if (!haystack.includes(kw)) return false;
    }
    return true;
  });
  return sortMovies(result, sort);
}

export interface CategoryConfig {
  title: string;
  description: string;
  defaultType: "movie" | "tv" | "";
  defaultGenre?: string;
  defaultRegion?: string;
}

const CATEGORY_MAP: Record<string, CategoryConfig> = {
  movie: {
    title: "电影",
    description: "汇聚院线热映与经典佳片，动作、科幻、爱情、悬疑多类型高清电影，持续更新。",
    defaultType: "movie",
  },
  tv: {
    title: "电视剧",
    description: "热播剧集同步追更，国产、欧美、日韩剧集分类清晰，剧集进度一目了然。",
    defaultType: "tv",
  },
  action: {
    title: "动作片",
    description: "热血动作、极限追逐与硬核打斗，精选高能动作影视作品。",
    defaultType: "",
    defaultGenre: "动作",
  },
  romance: {
    title: "爱情片",
    description: "甜蜜心动与虐心虐恋并存，精选爱情题材电影与电视剧。",
    defaultType: "",
    defaultGenre: "爱情",
  },
  scifi: {
    title: "科幻片",
    description: "想象无界的科幻世界，太空、未来与科技题材影视一网打尽。",
    defaultType: "",
    defaultGenre: "科幻",
  },
  suspense: {
    title: "悬疑片",
    description: "烧脑推理与层层反转，精选高口碑悬疑影视作品。",
    defaultType: "",
    defaultGenre: "悬疑",
  },
  china: {
    title: "国产剧集",
    description: "聚焦国产优秀影视作品，都市、古装、刑侦、年代多题材全覆盖。",
    defaultType: "",
    defaultRegion: "中国",
  },
  hollywood: {
    title: "欧美影视",
    description: "精选欧美电影与剧集，科幻、动作、剧情大制作持续更新。",
    defaultType: "",
    defaultRegion: "欧美",
  },
};

export function resolveCategory(type: string): CategoryConfig {
  return (
    CATEGORY_MAP[type] || {
      title: "影视分类",
      description: "浏览全部影视作品，支持按类型、地区、年份筛选。",
      defaultType: "",
    }
  );
}

export function getCategoryHref(key: string): string {
  return `/category/${key}`;
}